import { DndContext, DragOverlay, closestCenter, pointerWithin, KeyboardSensor, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, arrayMove, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";
import { api } from "@/lib/api";

export const SortableList = ({ ids, onReorder, children }) => {
  const sensors = useBoardSensors();
  const onDragEnd = ({ active, over }) => over && active.id !== over.id && onReorder(active.id, over.id);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext>
    </DndContext>
  );
};

const useBoardSensors = () =>
  useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

// Prefer item under pointer, then container under pointer, then nearest.
const boardCollision = (args) => {
  const hits = pointerWithin(args);
  if (hits.length) return [hits.find((h) => !String(h.id).startsWith("list:")) || hits[0]];
  return closestCenter(args);
};

export const DndBoard = ({ onDragStart, onDragOver, onDragEnd, overlay, children }) => {
  const sensors = useBoardSensors();
  return (
    <DndContext sensors={sensors} collisionDetection={boardCollision} onDragStart={onDragStart} onDragOver={onDragOver}
      onDragEnd={onDragEnd} onDragCancel={() => onDragEnd({})}>
      {children}
      <DragOverlay dropAnimation={null}>{overlay}</DragOverlay>
    </DndContext>
  );
};

export const SortableGroup = ({ ids, children }) => (
  <SortableContext items={ids} strategy={verticalListSortingStrategy}>{children}</SortableContext>
);

export const useSortableRow = (id) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  return {
    ref: setNodeRef,
    style: { transform: CSS.Transform.toString(transform), transition, position: "relative", zIndex: isDragging ? 20 : undefined },
    handle: { ...attributes, ...listeners },
    isDragging,
  };
};

export const DragHandle = ({ handle, testId }) => (
  <button type="button" {...handle} data-testid={testId} aria-label="Drag to reorder"
    className="cursor-grab active:cursor-grabbing touch-none p-1 -ml-1 rounded-md text-t-faint hover:text-t-muted hover:bg-t-surface transition-colors">
    <GripVertical className="h-4 w-4" />
  </button>
);

export const reorderItems = async (path, doc, activeId, overId, onChange) => {
  const ids = doc.items.map((i) => i.id);
  const items = arrayMove(doc.items, ids.indexOf(activeId), ids.indexOf(overId));
  onChange({ ...doc, items });
  onChange((await api.put(`${path}/items/order`, { ids: items.map((i) => i.id) })).data);
};
